import styled from 'styled-components';
import { InputHTMLAttributes, forwardRef, useState } from 'react';
import { EyesIcon } from '../icon/icon';
import { formatPhoneNumber } from '../../utils/util';

interface JoinInputProps {
  name: string;
  label: string;
  errors?: string[];
  btn?: boolean;
  btnTitle?: string;
  register?: any;
  validation?: object;
  onClick?: () => void;
}

interface TextAreaInputProps {
  name: string;
  label?: string;
  errors?: string[];
  register?: any;
  className?: string;
}

export const Input = forwardRef<
  HTMLInputElement,
  JoinInputProps & InputHTMLAttributes<HTMLInputElement>
>(({ name, label, errors = [], register, className, ...rest }, ref) => {
  return (
    <div className={className}>
      <InputBox>
        <label htmlFor={name}>{label}</label>
        <div className="inner-box">
          <input
            name={name}
            ref={ref}
            {...rest}
            {...(register ? register(name) : {})} // register 함수 사용
          />
        </div>
      </InputBox>
      {errors.map((error, index) => (
        <ValTxt key={index}>{error}</ValTxt>
      ))}
    </div>
  );
});

export const JoinInput = ({
  name,
  label,
  errors = [],
  btn,
  btnTitle,
  register,
  validation = {},
  onClick,
  className,
  ...rest
}: JoinInputProps & InputHTMLAttributes<HTMLInputElement>) => {
  return (
    <div className={className}>
      <InputBox>
        <label htmlFor={name}>{label}</label>
        <div className="inner-box">
          <input name={name} {...rest} {...(register ? register(name) : {})} />
          {btn ? (
            <button type="button" onClick={onClick}>
              {btnTitle ? btnTitle : '중복확인'}
            </button>
          ) : null}
        </div>
      </InputBox>
      {errors.map((error, index) => (
        <ValTxt key={index}>{error}</ValTxt>
      ))}
    </div>
  );
};

export const JoinPhoneInput = ({
  name,
  label,
  errors = [],
  btn,
  btnTitle,
  register,
  validation = {},
  onClick,
  className,
  ...rest
}: JoinInputProps & InputHTMLAttributes<HTMLInputElement>) => {
  return (
    <div className={className}>
      <InputBox>
        <label htmlFor={name}>{label}</label>
        <div className="inner-box">
          <input
            name={name}
            {...rest}
            {...register(name, {
              ...validation,
              onChange: (e: any) => {
                const formattedValue = formatPhoneNumber(e.target.value);
                e.target.value = formattedValue; // 포맷팅된 값으로 input 값 설정
                rest.onChange && rest.onChange(e); // 기존의 onChange가 있으면 호출
              },
            })}
          />
          {btn ? (
            <button type="button" onClick={onClick}>
              {btnTitle ? btnTitle : '중복확인'}
            </button>
          ) : null}
        </div>
      </InputBox>
      {errors.map((error, index) => (
        <ValTxt key={index}>{error}</ValTxt>
      ))}
    </div>
  );
};

export const LoginInput = ({
  name,
  label,
  errors = [],
  btn,
  register,
  validation = {},
  type = 'text',
  ...rest
}: JoinInputProps & InputHTMLAttributes<HTMLInputElement>) => {
  const [inputType, setInputType] = useState(type); // 초기값을 type으로 설정
  const handleToggleType = () => {
    setInputType((prevType) => (prevType === 'password' ? 'text' : 'password'));
  };

  return (
    <div>
      <LoginInputBox $inputType={inputType}>
        <label htmlFor={name}>{label}</label>
        <div className="inner-box">
          <input
            name={name}
            {...rest}
            type={inputType}
            {...register(name, validation)}
          />
        </div>
        {btn ? (
          <button className="eyes-btn" type="button" onClick={handleToggleType}>
            <EyesIcon />
          </button>
        ) : null}
      </LoginInputBox>

      {errors.map((error, index) => (
        <ValTxt key={index}>{error}</ValTxt>
      ))}
    </div>
  );
};

export const TextArea = ({
  name,
  label,
  errors = [],
  register,
  onClick,
  className,
  ...rest
}: TextAreaInputProps & InputHTMLAttributes<HTMLTextAreaElement>) => {
  return (
    <div className={className}>
      <TextareaBox>
        {label ? <label htmlFor={name}>{label}</label> : null}
        <div className="inner-box">
          <textarea
            name={name}
            {...rest}
            {...(register ? register(name) : {})}
          />
        </div>
      </TextareaBox>
      {errors.map((error, index) => (
        <ValTxt key={index}>{error}</ValTxt>
      ))}
    </div>
  );
};

//style
const InputBox = styled.div`
  padding: 1.1rem 1.1rem 1.6rem 1.5rem;
  background-color: ${(props) => props.theme.gray100};
  border-radius: 0.8rem;
  display: flex;
  flex-direction: column;
  label {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${(props) => props.theme.joinLabelColor};
    margin-bottom: 0.6rem;
    line-height: 1.5rem;
  }
  .inner-box {
    display: flex;
    align-items: flex-end;
    gap: 0.4rem;
    min-height: 2.4rem;
    input:not([type='submit']) {
      width: 100%;
      font-size: 1.6rem;
      font-weight: 400;
      background-color: inherit;
      border: 0;
      outline: none;
      &::placeholder {
        color: ${(props) => props.theme.placeholderColor};
      }
    }
    input:disabled {
      color: ${(props) => props.theme.gray100} !important;
    }
    input:-internal-autofill-selected {
      background-color: transparent !important;
    }
    input:read-only {
      color: ${(props) => props.theme.gray900};
    }

    button {
      padding: 0.4rem 0.8rem;
      flex-basis: content;
      flex-shrink: 0;
      border: 0;
      outline: none;
      color: ${(props) => props.theme.textWhiteColor};
      background-color: ${(props) => props.theme.btnBlueColor};
      border-radius: 0.4rem;
      font-size: 1.3rem;
      font-weight: 400;
    }
  }
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 1rem 1.7rem 0;
`;

const LoginInputBox = styled(InputBox)<{ $inputType: string }>`
  border-radius: 0;
  background-color: ${(props) => props.theme.textWhiteColor};
  border-bottom: 1px solid ${(props) => props.theme.gray300};
  position: relative;
  .eyes-btn {
    position: absolute;
    outline: none;
    border: 0;
    padding: 0;
    background: transparent;
    right: 1.2rem;
    bottom: 1.6rem;
    svg {
      path {
        fill: ${({ $inputType }) =>
          $inputType === 'password' ? '#D8D7DC' : 'black'};
      }
    }
  }
`;

// Styles for JoinTextarea component
const TextareaBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
  }

  .inner-box {
    position: relative;
    textarea {
      display: flex;
      width: 100%;
      min-height: 6.5rem;
      padding: 1.4rem 1.8rem;
      border: 1px solid #d6dbe1;
      border-radius: 0.8rem;
      resize: vertical;
      font-size: 1.4rem;
      font-weight: 400;
      resize: none;
      &::placeholder {
        color: ${(props) => props.theme.searchPlaceholderColor};
      }
    }
  }
`;
